/**
 * Feature flags for parts of the UI that are built but not ready to be used.
 *
 * These hide UI only — the corresponding server endpoints are untouched and
 * still governed by the normal role/tenancy checks. A flag being `false` is
 * not an access control.
 *
 * To enable a feature permanently: change its default below to `true`.
 * To enable it for a single build: set the matching environment variable,
 * e.g. `VITE_FEATURE_EVENT_CLASSES=true npm run build`.
 *
 * Note that `.env*` files are gitignored in this repo (only `.env.example` is
 * tracked), so the defaults below are what CI-built bundles use. The env vars
 * are an override for local builds or a CI-injected value — not a committed
 * per-environment config. See `client/.env.example` for the variable names.
 */
/**
 * An empty string counts as "not set", not as "false".
 *
 * The Dockerfile declares `ARG VITE_SHOW_LOCAL_LOGIN` and then unconditionally
 * `ENV VITE_SHOW_LOCAL_LOGIN=$VITE_SHOW_LOCAL_LOGIN`. Build without passing
 * that arg — which is the normal case — and the variable is defined as `''`
 * rather than left unset, so Vite inlines an empty string and a plain
 * `value === undefined` check treats it as an explicit `false`. That silently
 * defeated the `true` default for `showLocalLogin` and shipped a login page
 * offering only the Microsoft button, with no local sign-in and therefore no
 * break-glass path into the deployed app. Treating `''` as absent makes the
 * declared default hold however the image is built.
 */
export const flag = (value?: string, defaultValue = false): boolean =>
  value === undefined || value === '' ? defaultValue : value === 'true';

export const features = {
  /** Event → Classes tab */
  eventClasses: flag(import.meta.env.VITE_FEATURE_EVENT_CLASSES),
  /** Event → Packages tab */
  eventPackages: flag(import.meta.env.VITE_FEATURE_EVENT_PACKAGES),
  /**
   * Event → Registrations tab, the registration detail page, and the event
   * export page. Export is grouped here because the Registrations tab is its
   * only entry point; give it its own flag if that ever stops being true.
   */
  eventRegistrations: flag(import.meta.env.VITE_FEATURE_EVENT_REGISTRATIONS),
  /** Entra sign-in on the login page. Server-side, ENTRA_ENABLED gates the endpoint. */
  entraAuth: flag(import.meta.env.VITE_ENTRA_CLIENT_ID ? 'true' : undefined),
  /**
   * Whether the username/password form is offered on the login page.
   *
   * Cosmetic only. The control that actually stops an Entra user signing in
   * with a password is the per-account guard in POST /api/auth/login.
   */
  showLocalLogin: flag(import.meta.env.VITE_SHOW_LOCAL_LOGIN, true),
};

export type FeatureName = keyof typeof features;
